/**
 * The Page menu: the paper, which way up it is, how large the staves are, how much white
 * is left around and between them, how often the bars are numbered, and whether silent
 * bars are gathered.
 *
 * A menu can only offer choices, so the sizes are offered as the papers and staff heights
 * anyone actually asks for, and the two distances between staves step by a couple of
 * millimetres at a time. The editing layer holds the bounds; a step past one comes back as
 * a sentence and the page is left alone, which is why nothing here checks anything.
 */

import { PAGE_SIZES, layout as page } from "@app/score-editor";
import type { BarNumbers } from "@app/score-editor";
import type { ScoreActions } from "./score-actions";
import { RULE } from "./score-menu-model";
import type { Menu, MenuEntry } from "./score-menu-model";

/**
 * The staff heights offered, in millimetres.
 *
 * They are the printed rastral sizes an engraver names: a pocket score at the bottom, a
 * teaching edition at the top, and the ordinary printed page in the middle.
 */
const STAFF_HEIGHTS: readonly { millimetres: number; label: string }[] = [
  { millimetres: 3.7, label: "Pocket score" },
  { millimetres: 4.5, label: "Study score" },
  { millimetres: 5.5, label: "Small" },
  { millimetres: 6.5, label: "Printed part" },
  { millimetres: 7, label: "Normal" },
  { millimetres: 8, label: "Large" },
  { millimetres: 9.2, label: "Teaching edition" },
];

/** The borders offered, in millimetres on all four sides. */
const MARGINS: readonly { millimetres: number; label: string }[] = [
  { millimetres: 10, label: "Narrow" },
  { millimetres: 15, label: "Normal" },
  { millimetres: 20, label: "Wide" },
  { millimetres: 25, label: "Very wide" },
];

/**
 * How often the bars are numbered.
 *
 * Each system is what a printed score does; every bar is what a rehearsal takes; the two
 * counts are the ones an engraver reaches for on a piece too long to number in full. A
 * count off this list is still allowed -- the editing layer takes any up to 64 -- but a
 * menu has to name the ones people ask for.
 */
const NUMBERINGS: readonly { value: BarNumbers; label: string }[] = [
  { value: "none", label: "No bar numbers" },
  { value: "system", label: "Bar number on each system" },
  { value: 1, label: "Bar number on every bar" },
  { value: 5, label: "Bar number every fifth bar" },
  { value: 10, label: "Bar number every tenth bar" },
];

const STEP = 2;

/** The paper, the staff size and the space around the music. */
export function pageMenu(actions: ScoreActions): Menu {
  const { score } = actions;
  const set = (patch: Parameters<typeof page.set>[1], label: string) => (): void => {
    actions.edit(page.set(score, patch), label);
  };

  const here = score.layout;
  const landscape = here.pageWidth > here.pageHeight;
  const named = page.pageSizeOf(score);

  const papers: MenuEntry[] = PAGE_SIZES.map((size) => ({
    label: `${size.name} — ${size.width} × ${size.height} mm`,
    checked: named?.name === size.name,
    run: set(
      landscape
        ? { pageWidth: size.height, pageHeight: size.width }
        : { pageWidth: size.width, pageHeight: size.height },
      "page size",
    ),
  }));

  const staves: MenuEntry[] = STAFF_HEIGHTS.map((size) => ({
    label: `${size.label} — ${size.millimetres} mm staff`,
    checked: Math.abs(here.staffHeight - size.millimetres) < 0.05,
    run: set({ staffHeight: size.millimetres }, "staff size"),
  }));

  const margins: MenuEntry[] = MARGINS.map((margin) => ({
    label: `${margin.label} margins — ${margin.millimetres} mm`,
    checked: here.margins.left === margin.millimetres && here.margins.top === margin.millimetres,
    run: set(
      {
        margins: {
          left: margin.millimetres,
          right: margin.millimetres,
          top: margin.millimetres,
          bottom: margin.millimetres,
        },
      },
      "margins",
    ),
  }));

  const numbering: MenuEntry[] = NUMBERINGS.map((choice) => ({
    label: choice.label,
    checked: here.barNumbers === choice.value,
    run: () => actions.edit(page.setBarNumbers(score, choice.value), "bar numbers"),
  }));

  return {
    title: "Page",
    items: [
      ...papers,
      RULE,
      {
        label: "Portrait",
        checked: !landscape,
        run: () => actions.edit(page.setLandscape(score, false), "orientation"),
      },
      {
        label: "Landscape",
        checked: landscape,
        run: () => actions.edit(page.setLandscape(score, true), "orientation"),
      },
      RULE,
      ...staves,
      RULE,
      ...margins,
      RULE,
      {
        label: "Staves further apart",
        run: set({ staffGap: here.staffGap + STEP }, "staff spacing"),
      },
      {
        label: "Staves closer together",
        run: set({ staffGap: here.staffGap - STEP }, "staff spacing"),
      },
      {
        label: "Systems further apart",
        run: set({ systemGap: here.systemGap + STEP }, "system spacing"),
      },
      {
        label: "Systems closer together",
        run: set({ systemGap: here.systemGap - STEP }, "system spacing"),
      },
      RULE,
      ...numbering,
      RULE,
      {
        // A part gathers its rests and a score does not, so this is already on in a part
        // taken out of a score. It is here for the reader who wants it the other way.
        label: "Count the silent bars",
        checked: here.multiRests,
        run: () => actions.edit(page.setMultiRests(score, !here.multiRests), "multi-bar rests"),
      },
      {
        // The page a conductor reads, and a page that cannot be written on: a staff that
        // is not drawn cannot be clicked. The status bar says so for as long as it is on,
        // which is where a user looks when a part has gone.
        label: "Hide the staves that are silent",
        checked: here.hideEmptyStaves,
        run: () =>
          actions.edit(
            page.setHideEmptyStaves(score, !here.hideEmptyStaves),
            "hiding the silent staves",
          ),
      },
    ],
  };
}
